import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, RadioGroup } from '@mui/material';
import { useQuery, useMutation } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { projectSubscriptionsService } from '../../services/ProjectSubscriptionsService';
import { Subscription } from '../../types/Subscription';
import { ROUTES_URL } from '../../constants/routes';
import SubscriptionItem from '../../widgets/SubscriptionItem';
import useIsMobileSize from '../../hooks/useIsMobileSize';
import { WithoutHeader, DesktopLayout } from '../../layout';

const ProjectPayment: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const location = useLocation();
    const isMobile = useIsMobileSize();

    const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
    const [duration, setDuration] = useState(1);

    const projectId = new URLSearchParams(location.search).get('projectId');

    const { data: plans, isLoading: plansLoading } = useQuery(
        'subscription-plans',
        projectSubscriptionsService.getSubscriptionPlans
    );

    const createPaymentMutation = useMutation(projectSubscriptionsService.createPayment, {
        onSuccess: (data) => {
            window.location.href = data.confirmation_url;
        },
        onError: (error: any) => {
            enqueueSnackbar(error.response?.data?.error || 'Ошибка создания платежа', { variant: 'error' });
        },
    });

    const handlePayment = () => {
        if (!selectedPlan || !projectId) return;

        createPaymentMutation.mutate({
            project_id: parseInt(projectId),
            plan_id: selectedPlan,
            duration_months: duration,
        });
    };

    const renderContent = () => (
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100%"
            flexGrow={1}
        >
            <Box mb={3}>
                <Typography
                    variant="h1"
                    color="text.primary"
                >
                    Выбор тарифа для проекта
                </Typography>
            </Box>

            {plansLoading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    p={4}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <RadioGroup
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(Number(e.target.value))}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={2}
                    >
                        {plans?.map((plan) => (
                            <SubscriptionItem
                                key={plan.id}
                                value={plan.id}
                                checkedValue={selectedPlan}
                                price={plan.price}
                                title={plan.name}
                                bundles={plan.bundles_limit}
                                keywords={plan.keywords_limit}
                                chats={plan.chats_limit}
                                projects={1}
                                color={plan.color_code}
                                ai_analysis_enabled={plan.ai_analysis_enabled}
                                description={plan.description}
                            />
                        ))}
                    </Box>
                </RadioGroup>
            )}

            <Box
                mt={3}
                display="flex"
                gap={2}
            >
                <Button
                    variant="outlined"
                    onClick={() => history.goBack()}
                    fullWidth={isMobile}
                >
                    Назад
                </Button>
                <Button
                    variant="contained"
                    onClick={handlePayment}
                    disabled={!selectedPlan || createPaymentMutation.isLoading}
                    fullWidth={isMobile}
                >
                    {createPaymentMutation.isLoading ? 'Создание платежа...' : 'Оплатить'}
                </Button>
            </Box>
        </Box>
    );

    if (!isMobile) {
        return <DesktopLayout>{renderContent()}</DesktopLayout>;
    }

    return <WithoutHeader showBack>{renderContent()}</WithoutHeader>;
};

export default ProjectPayment;
